import { ModelView } from "../model.view";
import {
  DefaultSavingGoalStoreModel,
  SavingGoalModelVersion,
  SavingGoalStoreModel,
} from "@bitwarden/web-vault/app/models/store/saving-goal.store.model";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { SavingGoalTrackingMethod } from "@bitwarden/web-vault/app/models/types/saving-goal.types";

/** Not persisted — computed on demand by
 *  SavingGoalStoreService.contributionHistory() for whichever granularity (weekly/monthly)
 *  the budget page's period switcher is currently on, so it's never stale after an edit and
 *  always matches the selected granularity. */
export type SavingGoalContribution = {
  /** Display label, e.g. "June 2026" (monthly) or "Jun 1 – 7, 2026" (weekly) */
  period: string;
  start: number;
  end: number;
  added: number;
};

/**
 * Persisted view model for Saving Goals (gloss-client #4723) — synced to the vault the same
 * way BudgetView is (see budget.view.ts). Only the tracked entity itself is persisted:
 * `currentAmount` and the linked account's display name are deliberately NOT stored here since
 * they're derived from live balance/account data — see SavingGoalUtils.toProgress() and
 * BudgetComponent.savingGoalAccountName()/savingGoalAccountBalance(). Storing them would go
 * stale the moment the underlying balance changes.
 */
export class SavingGoalView extends ModelView<SavingGoalStoreModel> {
  protected version: SavingGoalModelVersion;
  name: string;
  targetAmount: number;
  currency?: CurrencyType | null;
  /** When the goal was created. Not user-editable (no form field for it, matching the design),
   *  set once at creation and carried through unchanged on every edit. Contribution history is
   *  bucketed from this date, not a fixed trailing window. */
  startDate: Date;
  targetDate?: Date | null;
  /** The single tracked account — no multi-source support yet (see the Figma "Add Source is
   *  not in this version" annotation on node 2098:1525). */
  accountId: string;
  trackingMethod: SavingGoalTrackingMethod;
  /** Set when trackingMethod === "growthAboveBaseline" — balance above this doesn't count. */
  baselineToHoldBack?: number | null;
  /** Set when trackingMethod === "percentageOfBalance" — 0-100. */
  percentageOfBalance?: number | null;

  constructor(model?: SavingGoalStoreModel) {
    super(model);

    if (typeof model === "object") {
      this.name = model.nm;
      this.targetAmount = model.ta;
      this.currency = model.cur;
      this.startDate = model.sdt ? new Date(model.sdt) : null;
      this.targetDate = model.tdt ? new Date(model.tdt) : null;
      this.accountId = model.acId;
      this.trackingMethod = model.trm;
      this.baselineToHoldBack = model.bhb;
      this.percentageOfBalance = model.pob;
    } else {
      this.version = DefaultSavingGoalStoreModel.v;
      this.currency = DefaultSavingGoalStoreModel.cur;
      this.trackingMethod = DefaultSavingGoalStoreModel.trm;
    }
  }

  toStoreModel(): SavingGoalStoreModel {
    return {
      id: this.id,
      vid: this.vaultId,
      v: this.version,
      dc: this.dateCreated.toISOString(),
      dm: this.dateModified.toISOString(),
      nm: this.name,
      ta: this.targetAmount,
      cur: this.currency,
      sdt: this.startDate,
      tdt: this.targetDate,
      acId: this.accountId,
      trm: this.trackingMethod,
      bhb: this.baselineToHoldBack,
      pob: this.percentageOfBalance,
    };
  }

  clone(): SavingGoalView {
    return new SavingGoalView(this.toStoreModel());
  }
}
